// A small floating panel of live controls (sliders, switches, choices, readouts), plain DOM so it has nothing to do with the page's React tree.
//
// Built for tuning the clouds while the scene runs: every control calls its handler as it moves, nothing needs a reload or the console. Values the person
// changed are remembered in the browser (localStorage, one key) and put back on the next load; a control left alone follows the shipped default, so a new
// default still reaches it. "Copy" puts the changed values on the clipboard as JSON (to bake favourites into the defaults); "Reset" puts every control back.
//
// It opens from its tab at the screen's edge or from a key (default Backslash), lets go of the pointer lock when it opens (the scene walks with the mouse
// and keys), and keeps its own keys from reaching the scene's walking handlers.

type Val = number | boolean | string;

export interface TunePanelOptions {
  /** localStorage key the changed values are kept under. */
  storageKey: string;
  title: string;
  /** KeyboardEvent.code that opens and closes it. */
  toggleCode?: string;
  /** Called when it opens, so the page can give the pointer back. */
  onOpen?: () => void;
}

interface Ctl { id: string; def: Val; apply: (v: Val) => void; set: (v: Val) => void }

const CSS = `
.nt-tab{position:fixed;right:0;top:38%;z-index:2147483000;writing-mode:vertical-rl;transform:rotate(180deg);background:rgba(18,22,30,.82);color:#cfe3ff;font:600 12px/1 system-ui,sans-serif;letter-spacing:.08em;padding:12px 7px;border-radius:0 8px 8px 0;cursor:pointer;user-select:none;border:1px solid rgba(160,190,255,.25);border-left:none}
.nt-tab:hover{background:rgba(32,40,56,.92)}
.nt-panel{position:fixed;right:8px;top:8px;bottom:8px;width:300px;max-width:calc(100vw - 16px);z-index:2147483001;display:none;flex-direction:column;background:rgba(14,18,26,.9);color:#dbe6f7;font:12px/1.35 system-ui,sans-serif;border:1px solid rgba(160,190,255,.25);border-radius:10px;backdrop-filter:blur(6px);box-shadow:0 8px 30px rgba(0,0,0,.45)}
.nt-panel.nt-open{display:flex}
.nt-head{display:flex;align-items:center;gap:6px;padding:8px 10px;border-bottom:1px solid rgba(160,190,255,.18)}
.nt-head b{flex:1;font-size:13px;letter-spacing:.04em}
.nt-btn{background:rgba(70,100,160,.35);color:#dbe6f7;border:1px solid rgba(160,190,255,.3);border-radius:6px;padding:3px 8px;font:inherit;cursor:pointer}
.nt-btn:hover{background:rgba(90,130,200,.5)}
.nt-body{overflow-y:auto;padding:4px 10px 12px;flex:1}
.nt-sec{margin:12px 0 4px;font-weight:700;color:#9fc4ff;text-transform:uppercase;font-size:11px;letter-spacing:.09em;border-bottom:1px solid rgba(160,190,255,.15);padding-bottom:3px}
.nt-row{display:grid;grid-template-columns:96px 1fr 52px;align-items:center;gap:6px;margin:4px 0}
.nt-row.nt-tog,.nt-row.nt-sel{grid-template-columns:1fr auto}
.nt-row label{color:#c5d3e8}
.nt-row input[type=range]{width:100%;margin:0;accent-color:#7fb0ff}
.nt-val{text-align:right;font-variant-numeric:tabular-nums;color:#fff}
.nt-row select{background:#1d2535;color:#dbe6f7;border:1px solid rgba(160,190,255,.3);border-radius:5px;padding:2px 4px;font:inherit}
.nt-changed label::after{content:" •";color:#ffcf6b}
.nt-note{color:#8da0bb;font-size:11px;margin:-2px 0 4px}
.nt-ro{display:flex;justify-content:space-between;gap:8px;margin:3px 0;color:#c5d3e8}
.nt-ro span:last-child{color:#fff;font-variant-numeric:tabular-nums;text-align:right}
`;

export class TunePanel {
  private root: HTMLDivElement;
  private tab: HTMLDivElement;
  private body: HTMLDivElement;
  private style: HTMLStyleElement;
  private stored: Record<string, Val> = {};
  private ctls: Ctl[] = [];
  private readouts: Array<{ el: HTMLElement; fn: () => string }> = [];
  private timer = 0;
  private openState = false;
  private onKey: (e: KeyboardEvent) => void;

  constructor(private opts: TunePanelOptions) {
    try { const s = localStorage.getItem(opts.storageKey); if (s) this.stored = JSON.parse(s) ?? {}; } catch { this.stored = {}; }
    this.style = document.createElement('style'); this.style.textContent = CSS; document.head.appendChild(this.style);
    this.tab = document.createElement('div'); this.tab.className = 'nt-tab'; this.tab.textContent = opts.title; this.tab.onclick = () => this.setOpen(true);
    this.root = document.createElement('div'); this.root.className = 'nt-panel';
    const head = document.createElement('div'); head.className = 'nt-head';
    const title = document.createElement('b'); title.textContent = opts.title;
    const copy = this.button('Copy', () => this.copy()); copy.title = 'Copy the values you changed, as JSON';
    const reset = this.button('Reset', () => this.reset()); reset.title = 'Put every control back to its default';
    const close = this.button('×', () => this.setOpen(false));
    head.append(title, copy, reset, close);
    this.body = document.createElement('div'); this.body.className = 'nt-body';
    this.root.append(head, this.body);
    document.body.append(this.tab, this.root);
    // keep the scene's walking keys and mouse handlers out of the panel's way
    const stop = (e: Event) => e.stopPropagation();
    for (const ev of ['keyup', 'keypress', 'wheel', 'contextmenu']) this.root.addEventListener(ev, stop);
    this.root.addEventListener('keydown', e => { if (e.code === (opts.toggleCode ?? 'Backslash') && !e.repeat) this.setOpen(false); e.stopPropagation(); });
    this.onKey = (e: KeyboardEvent) => {
      if (e.code !== (opts.toggleCode ?? 'Backslash') || e.ctrlKey || e.metaKey || e.altKey || e.repeat) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable) && !this.root.contains(t)) return;
      this.setOpen(!this.openState);
    };
    window.addEventListener('keydown', this.onKey);
  }

  get isOpen() { return this.openState; }

  setOpen(on: boolean) {
    this.openState = on;
    this.root.classList.toggle('nt-open', on);
    this.tab.style.display = on ? 'none' : '';
    if (on) { this.opts.onOpen?.(); this.refresh(); this.timer = window.setInterval(() => this.refresh(), 250); }
    else if (this.timer) { clearInterval(this.timer); this.timer = 0; }
  }

  private refresh() {
    for (const r of this.readouts) { try { r.el.textContent = r.fn(); } catch { r.el.textContent = '-'; } }
  }

  private save() {
    try { if (Object.keys(this.stored).length) localStorage.setItem(this.opts.storageKey, JSON.stringify(this.stored)); else localStorage.removeItem(this.opts.storageKey); } catch { /* storage blocked */ }
  }

  private changed(id: string, def: Val, v: Val) {
    if (v === def) delete this.stored[id]; else this.stored[id] = v;
    this.save();
  }

  private button(text: string, fn: () => void) {
    const b = document.createElement('button'); b.className = 'nt-btn'; b.textContent = text; b.onclick = fn; return b;
  }

  private copy() {
    const text = JSON.stringify(this.stored, null, 2);
    const done = () => { /* nothing to confirm beyond the clipboard */ };
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(text).then(done, () => window.prompt('Changed values', text));
    else window.prompt('Changed values', text);
  }

  /** Forget remembered values for controls that no longer exist (so Copy does not list them). Call once every control is made. */
  prune() {
    const ids = new Set(this.ctls.map(c => c.id));
    let n = 0;
    for (const k of Object.keys(this.stored)) if (!ids.has(k)) { delete this.stored[k]; n++; }
    if (n) this.save();
  }

  reset() {
    this.stored = {}; this.save();
    for (const c of this.ctls) { c.apply(c.def); c.set(c.def); }
  }

  // -- controls ------------------------------------------------------------------------------------------------------------------------

  section(title: string, note?: string) {
    const s = document.createElement('div'); s.className = 'nt-sec'; s.textContent = title; this.body.appendChild(s);
    if (note) this.note(note);
  }

  note(text: string) {
    const n = document.createElement('div'); n.className = 'nt-note'; n.textContent = text; this.body.appendChild(n);
  }

  slider(id: string, label: string, o: { min: number; max: number; step: number; def: number; value?: number; digits?: number; onChange: (v: number) => void; title?: string }) {
    const row = document.createElement('div'); row.className = 'nt-row'; if (o.title) row.title = o.title;
    const l = document.createElement('label'); l.textContent = label;
    const inp = document.createElement('input'); inp.type = 'range'; inp.min = String(o.min); inp.max = String(o.max); inp.step = String(o.step);
    const val = document.createElement('span'); val.className = 'nt-val';
    const digits = o.digits ?? Math.max(0, Math.ceil(-Math.log10(o.step)));
    const show = (v: number) => { inp.value = String(v); val.textContent = v.toFixed(digits); row.classList.toggle('nt-changed', v !== o.def); };
    inp.oninput = () => { const v = parseFloat(inp.value); show(v); o.onChange(v); this.changed(id, o.def, v); };
    row.append(l, inp, val); this.body.appendChild(row);
    const st = this.stored[id];
    if (typeof st === 'number') { show(st); o.onChange(st); } else show(o.value ?? o.def);
    this.ctls.push({ id, def: o.def, apply: v => o.onChange(v as number), set: v => show(v as number) });
  }

  toggle(id: string, label: string, o: { def: boolean; value?: boolean; onChange: (v: boolean) => void; title?: string }) {
    const row = document.createElement('div'); row.className = 'nt-row nt-tog'; if (o.title) row.title = o.title;
    const l = document.createElement('label'); l.textContent = label;
    const inp = document.createElement('input'); inp.type = 'checkbox';
    const show = (v: boolean) => { inp.checked = v; row.classList.toggle('nt-changed', v !== o.def); };
    inp.onchange = () => { show(inp.checked); o.onChange(inp.checked); this.changed(id, o.def, inp.checked); };
    row.append(l, inp); this.body.appendChild(row);
    const st = this.stored[id];
    if (typeof st === 'boolean') { show(st); o.onChange(st); } else show(o.value ?? o.def);
    this.ctls.push({ id, def: o.def, apply: v => o.onChange(v as boolean), set: v => show(v as boolean) });
  }

  select(id: string, label: string, options: Array<[string, string]>, o: { def: string; value?: string; onChange: (v: string) => void; title?: string }) {
    const row = document.createElement('div'); row.className = 'nt-row nt-sel'; if (o.title) row.title = o.title;
    const l = document.createElement('label'); l.textContent = label;
    const sel = document.createElement('select');
    for (const [v, t] of options) { const op = document.createElement('option'); op.value = v; op.textContent = t; sel.appendChild(op); }
    const show = (v: string) => { sel.value = v; row.classList.toggle('nt-changed', v !== o.def); };
    sel.onchange = () => { show(sel.value); o.onChange(sel.value); this.changed(id, o.def, sel.value); };
    row.append(l, sel); this.body.appendChild(row);
    const st = this.stored[id];
    if (typeof st === 'string' && options.some(x => x[0] === st)) { show(st); o.onChange(st); } else show(o.value ?? o.def);
    this.ctls.push({ id, def: o.def, apply: v => o.onChange(v as string), set: v => show(v as string) });
  }

  /** A line of live text, refreshed four times a second while the panel is open. */
  readout(label: string, fn: () => string) {
    const row = document.createElement('div'); row.className = 'nt-ro';
    const a = document.createElement('span'); a.textContent = label; const b = document.createElement('span'); b.textContent = '-';
    row.append(a, b); this.body.appendChild(row);
    this.readouts.push({ el: b, fn });
  }

  dispose() {
    if (this.timer) clearInterval(this.timer);
    window.removeEventListener('keydown', this.onKey);
    this.root.remove(); this.tab.remove(); this.style.remove();
  }
}
